// The criteria failing most often, with the agents driving those
// failures. Rendered as the "Flagged Agents per Criteria" table the
// prior dashboard used -- coaches read this surface by column, and the
// card-list rewrite it briefly had was the thing they missed most.
//
// What the classic table never had, and this keeps: the criterion name
// drills into the criterion drawer, and an expanded agent drills into
// that same drawer narrowed to them.
import React, { useContext, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { isEmpty } from 'lodash'

import { Card } from '@/components/cards/Card'
import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import { formatPercent, truncatedTooltip } from '@/utils/helpers'
import { CHANNEL_NAME_PLURAL_DISPLAY, CHANNEL_TYPE } from '@/utils/constants'
import { ExternalCallIdsLink } from '@/views/QACopilot/components/ExternalCallIdsLink'

import { TopInfractorsRow } from '../components/TopInfractorsRow'
import { EntityClickContext } from '../helpers/dashboardContexts'

const MAX_CRITERIA_ROWS = 10

// Reusable single-card shell so loading/empty/rendered states share the
// same header + layout instead of duplicating <Card title="..."> three
// times.
const FlaggedCriteriaCard = ({ children }) => (
  <Card
    title="Flagged Agents per Criteria"
    info="The criteria failing most often, each with the agents driving those failures. Click a criterion to drill in, or a call count to open those calls in Call Explorer."
  >
    {children}
  </Card>
)

export const FlaggedCriteriaWidget = () => {
  const criteria = useSelector((state) => state.qaCopilot.data.nonCompliantCriteria)
  const loading = useSelector((state) => state.qaCopilot.loading.nonCompliantCriteria)
  const clickEntity = useContext(EntityClickContext)
  // Not in qaCopilot's initial data -- only set once filters apply. Without
  // the default, first render drops the channel scope off the Call Explorer
  // links and leaves the column headers undefined. Same default as
  // OverviewCardsRow.
  const appliedCallType =
    useSelector((state) => state.qaCopilot.data.appliedCallType) || CHANNEL_TYPE.CALL
  const channelLabel = CHANNEL_NAME_PLURAL_DISPLAY[appliedCallType]

  const openCriterion = (criterion, agentContext) => {
    if (!clickEntity) {
      return
    }
    clickEntity({ type: 'criterion', payload: { ...criterion, ...agentContext } })
  }

  const columns = [
    { accessor: 'criteria_name', label: 'Criteria' },
    {
      accessor: 'scorecard_config_name',
      label: 'Scorecard',
      format: (value) => truncatedTooltip(value),
    },
    // `call_count`/`call_ids` from the by_criteria feed are the FAILING
    // subset, not the scored population -- the API sets call_count from
    // failed_calls and builds call_ids from `criteria_score = 0`. The
    // classic table labelled this neutrally ("Calls"), which reads as a
    // total sitting next to a Fail Rate column; naming it plainly is
    // worth the small departure.
    { accessor: 'call_count', label: `Failing ${channelLabel}`, headerAlignment: 'right' },
    {
      accessor: 'failing_percent',
      label: 'Fail Rate',
      format: formatPercent,
      headerAlignment: 'right',
    },
    { accessor: 'top_infractors', label: 'Top Flagged Agents', isSortable: false },
  ]

  const rows = useMemo(() => {
    return (criteria || [])
      .slice()
      .sort((rowA, rowB) => rowB.call_count - rowA.call_count)
      .slice(0, MAX_CRITERIA_ROWS)
      .map((criterion) => {
        const criterionName = criterion.criteria_name || criterion.name || 'Unknown'
        return {
          ...criterion,
          rowKey: `${criterion.criteria_id}-${criterion.scorecard_config_sid || ''}`,
          criteria_name: {
            as: (
              <>
                <button
                  type="button"
                  className="table-link"
                  onClick={() => openCriterion(criterion)}
                >
                  {truncatedTooltip(criterionName, 25, 'top-start')}
                </button>
                {/* Stands in for the Scorecard column, which the CSS drops
                    when the cell is too narrow to carry five columns. Kept
                    outside the button so it never joins its label. */}
                <span className="flagged-criteria-inline-scorecard">
                  {criterion.scorecard_config_name}
                </span>
              </>
            ),
            value: criterionName,
          },
          call_count: {
            as: (
              <div className="text-right">
                <ExternalCallIdsLink
                  callIds={criterion.call_ids}
                  label={Number(criterion.call_count).toLocaleString()}
                  channelType={appliedCallType}
                  aria-label={`Open all failing ${criterionName} calls in Call Explorer`}
                />
              </div>
            ),
            value: criterion.call_count,
          },
          top_infractors: {
            as: (
              <TopInfractorsRow
                data={criterion}
                channelLabel={channelLabel}
                channelType={appliedCallType}
                // Same drilldown the card-list chips had: keep the criterion
                // focus the coach clicked from and ride the agent along.
                onAgentClick={(infractor, name) =>
                  openCriterion(criterion, {
                    agentContext: { user_id: infractor.user_id, full_name: name },
                  })
                }
              />
            ),
            value: 'top_infractors',
          },
        }
      })
  }, [criteria, appliedCallType, channelLabel, clickEntity])

  if (loading) {
    return (
      <FlaggedCriteriaCard>
        <div className="entity-drawer-empty">Loading…</div>
      </FlaggedCriteriaCard>
    )
  }

  if (isEmpty(rows)) {
    return (
      <FlaggedCriteriaCard>
        <div className="entity-drawer-empty">No flagged criteria in this window.</div>
      </FlaggedCriteriaCard>
    )
  }

  return (
    <FlaggedCriteriaCard>
      <div className="flagged-criteria-table">
        <AdvancedTable
          rows={rows}
          columns={columns}
          index="rowKey"
          defaultOrderBy="call_count"
          defaultOrder="desc"
          wrapColumnContent={false}
        />
      </div>
    </FlaggedCriteriaCard>
  )
}
